import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'default' | 'gradient' | 'outline' | 'outline-light';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  className?: string;
  children?: React.ReactNode;
  // ✅ Añadimos la propiedad que faltaba
  loading?: boolean;
}

const baseClasses =
  "inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-[14px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed gap-2";

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: { backgroundColor: '#0a65db', color: 'white', border: 'none' },
  secondary: { backgroundColor: '#e7ecf4', color: '#0d131c', border: 'none' },
  ghost: { backgroundColor: 'white', color: '#0d131c', border: '1px solid #cedae9' },
  default: { backgroundColor: '#e7ecf4', color: '#0d131c', border: 'none' },
  gradient: { background: 'linear-gradient(135deg, #001A70, #0063EA)', color: 'white', boxShadow: '0 4px 15px -3px rgba(0,99,234,0.3)', border: 'none' },
  // Sobre fondo claro: borde sky, texto sky → al hover se rellena de sky con texto blanco.
  outline: { backgroundColor: 'transparent', color: '#0063EA', border: '1.5px solid #0063EA' },
  // Sobre fondo de color (cards azul/indigo): borde blanco, texto blanco → al hover se rellena de blanco con texto sky.
  'outline-light': { backgroundColor: 'transparent', color: '#ffffff', border: '1.5px solid #ffffff' },
};

export const Button = ({
  variant = 'primary',
  className = '',
  children,
  style,
  loading = false, // ✅ Valor por defecto
  disabled,
  onMouseEnter,
  onMouseLeave,
  ...props
}: ButtonProps) => {
  const variantStyle = variantStyles[variant] ?? variantStyles.primary;

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'gradient') {
      e.currentTarget.style.boxShadow = '0 10px 20px -5px rgba(0,99,234,0.4)';
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.filter = 'brightness(1.1)';
    }
    if (variant === 'ghost') {
      e.currentTarget.style.borderColor = '#bfdbfe';
      e.currentTarget.style.backgroundColor = 'rgba(219,234,254,0.3)';
    }
    if (variant === 'outline') {
      e.currentTarget.style.backgroundColor = '#0063EA';
      e.currentTarget.style.color = 'white';
    }
    if (variant === 'outline-light') {
      e.currentTarget.style.backgroundColor = '#ffffff';
      e.currentTarget.style.color = '#0063EA';
    }
    onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'gradient') {
      e.currentTarget.style.boxShadow = '0 4px 15px -3px rgba(0,99,234,0.3)';
      e.currentTarget.style.transform = '';
      e.currentTarget.style.filter = '';
    }
    if (variant === 'ghost') {
      e.currentTarget.style.borderColor = '#cedae9';
      e.currentTarget.style.backgroundColor = 'white';
    }
    if (variant === 'outline') {
      e.currentTarget.style.backgroundColor = 'transparent';
      e.currentTarget.style.color = '#0063EA';
    }
    if (variant === 'outline-light') {
      e.currentTarget.style.backgroundColor = 'transparent';
      e.currentTarget.style.color = '#ffffff';
    }
    onMouseLeave?.(e);
  };

  return (
    <button
      className={`${baseClasses} ${className}`}
      style={{ transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.35s ease, background-color 0.35s ease', ...variantStyle, ...style }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // ✅ Si está cargando, el botón debe estar deshabilitado
      disabled={disabled || loading}
      {...props}
    >
      {/* ✅ Si está cargando, mostramos un pequeño spinner */}
      {loading && (
        <svg
          style={{ animation: 'spin 1s linear infinite', height: '1rem', width: '1rem' }}
          fill="none"
          viewBox="0 0 24 24"
        >
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};